(() => {
  if (customElements.get('cart-acknowledgements')) return;

  const STORAGE_KEY = 'sylavieCartAcknowledgements';
  const ACKNOWLEDGEMENT_TYPES = ['terms_and_returns', 'customs'];
  const SYNC_EVENT = 'sylavie:cart-acknowledgements-change';
  const EMPTY_EVENT = 'sylavie:cart-empty';
  const initialCartItemCount = Number(document.body?.dataset.cartItemCount);

  const getDefaultState = () => ({ terms_and_returns: false, customs: false });

  const readState = () => {
    try {
      const storedState = JSON.parse(window.localStorage.getItem(STORAGE_KEY));

      return {
        terms_and_returns:
          storedState?.terms_and_returns === true || (storedState?.terms === true && storedState?.returns === true),
        customs: storedState?.customs === true,
      };
    } catch (error) {
      return getDefaultState();
    }
  };

  const writeState = (state) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (error) {
      // The checkout gate still works when storage is unavailable.
    }
  };

  const clearState = () => {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      // The in-memory UI is still reset below when storage is unavailable.
    }

    window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: getDefaultState() }));
  };

  const isCartEmpty = () =>
    document.querySelector('cart-drawer.is-empty, #main-cart-footer.is-empty, cart-items.is-empty') !== null;

  class CartAcknowledgements extends HTMLElement {
    connectedCallback() {
      if (this.isConnectedAndInitialised) return;

      this.isConnectedAndInitialised = true;
      this.checkoutButton = this.parentElement?.querySelector('button[name="checkout"]');
      this.form = this.checkoutButton?.form;
      // Drawer sections replace their form and externally associated checkout
      // button. Delegate from the surviving drawer rather than caching its form.
      this.submitTarget = this.dataset?.context === 'drawer' ? this.closest('cart-drawer') : this.form;
      this.acceleratedCheckoutContainer = this.closest('.cart__blocks')?.querySelector(
        '.cart__dynamic-checkout-buttons'
      );
      this.onChange = this.onChange.bind(this);
      this.onSync = this.onSync.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onAcceleratedCheckout = this.onAcceleratedCheckout.bind(this);
      this.allowCheckoutOnce = false;

      this.addEventListener('change', this.onChange);
      window.addEventListener(SYNC_EVENT, this.onSync);
      this.submitTarget?.addEventListener('submit', this.onSubmit, true);
      this.acceleratedCheckoutContainer?.addEventListener('click', this.onAcceleratedCheckout, true);

      const state = readState();
      if (!this.querySelector('[data-acknowledgement="customs"]') && state.customs) {
        state.customs = false;
        writeState(state);
        window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: state }));
      } else {
        this.applyState(state);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      window.removeEventListener(SYNC_EVENT, this.onSync);
      this.submitTarget?.removeEventListener('submit', this.onSubmit, true);
      this.acceleratedCheckoutContainer?.removeEventListener('click', this.onAcceleratedCheckout, true);
      this.isConnectedAndInitialised = false;
    }

    onChange(event) {
      const input = event.target.closest('[data-acknowledgement]');
      if (!input) return;

      const state = readState();
      state[input.dataset.acknowledgement] = input.checked;
      writeState(state);
      window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: state }));
    }

    onSync(event) {
      this.applyState(event.detail || getDefaultState());
    }

    onSubmit(event) {
      if (this.dataset?.context === 'drawer') {
        this.checkoutButton = this.parentElement?.querySelector('button[name="checkout"]');
        this.form = this.checkoutButton?.form;
      }
      if (!this.checkoutButton || !this.form) return;

      const isStandardCheckout =
        event.submitter === this.checkoutButton ||
        (!event.submitter && ['cart', 'CartDrawer-Form', 'cart-notification-form'].includes(event.target?.id || event.currentTarget.id));

      if (!isStandardCheckout) return;

      if (!this.isComplete()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showErrors();
        return;
      }

      if (this.allowCheckoutOnce) {
        this.allowCheckoutOnce = false;
        return;
      }

      const internationalCheckoutModal = document.querySelector('international-checkout-modal');
      if (!internationalCheckoutModal) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      internationalCheckoutModal.open(this.checkoutButton, () => {
        this.allowCheckoutOnce = true;
        if (typeof this.form?.requestSubmit === 'function') {
          this.form.requestSubmit(this.checkoutButton);
        } else {
          this.checkoutButton?.click();
        }
      });
    }

    onAcceleratedCheckout(event) {
      if (this.isComplete()) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.showErrors();
    }

    applyState(state) {
      ACKNOWLEDGEMENT_TYPES.forEach((type) => {
        const input = this.querySelector(`[data-acknowledgement="${type}"]`);
        if (!input) return;

        input.checked = state[type] === true;
        if (input.checked) this.setError(type, false);
      });

      const isComplete = this.isComplete();
      if (this.checkoutButton) {
        this.checkoutButton.setAttribute('aria-disabled', String(!isComplete));
        this.checkoutButton.classList.toggle('disabled', !isComplete);
      }
      if (this.acceleratedCheckoutContainer) {
        this.acceleratedCheckoutContainer.setAttribute('aria-disabled', String(!isComplete));
        this.acceleratedCheckoutContainer.classList.toggle(
          'cart__dynamic-checkout-buttons--acknowledgements-required',
          !isComplete
        );
      }
      // Replacements and acknowledgement changes must retain the cart writer lock.
      window.CartMutations?.syncCheckout();
    }

    isComplete() {
      return ACKNOWLEDGEMENT_TYPES.every((type) => {
        const input = this.querySelector(`[data-acknowledgement="${type}"]`);
        return !input || input.checked;
      });
    }

    setError(type, shouldShow) {
      const item = this.querySelector(`[data-acknowledgement-item="${type}"]`);
      const input = item?.querySelector('[data-acknowledgement]');
      const error = item?.querySelector('.cart-acknowledgements__error');
      if (!input || !error) return;

      input.setAttribute('aria-invalid', String(shouldShow));
      error.hidden = !shouldShow;
      item.classList.toggle('cart-acknowledgements__item--error', shouldShow);
    }

    showErrors() {
      let firstInvalidInput = null;

      ACKNOWLEDGEMENT_TYPES.forEach((type) => {
        const input = this.querySelector(`[data-acknowledgement="${type}"]`);
        if (!input) return;

        const shouldShow = !input.checked;
        this.setError(type, shouldShow);
        if (shouldShow && !firstInvalidInput) firstInvalidInput = input;
      });

      firstInvalidInput?.focus();
    }
  }

  customElements.define('cart-acknowledgements', CartAcknowledgements);

  window.addEventListener(EMPTY_EVENT, clearState);
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: readState() }));
    }
  });

  if (initialCartItemCount === 0 || isCartEmpty()) clearState();
})();
